import axiosInstance from '../utils/axiosConfig';
import { AxiosError } from 'axios';
import { 
  Question, 
  PaginatedQuestionResponse,
  UnansweredQuestionsParams,
  
} from '../types/question';

export const questionApi = {

  getAllQuestions: async (
    page: number = 0,
    size: number = 9,
    selectedCategory: string[] = [],
    selectedDifficulty: string[] = [],
    selectedQuestionType: string[] = [],
    selectedVerificationStatus: string[] = []
  ) => {
    try {
      const response = await axiosInstance.get('/api/questions', {
        params: {
          page,
          size,
          selectedCategory: selectedCategory.length > 0 ? selectedCategory : undefined,
          selectedDifficulty: selectedDifficulty.length > 0 ? selectedDifficulty : undefined,
          selectedQuestionType: selectedQuestionType.length > 0 ? selectedQuestionType : undefined,
          selectedVerificationStatus: selectedVerificationStatus.length > 0 ? selectedVerificationStatus : undefined,
        },
        paramsSerializer: (params) => {
          // Custom serializer to handle arrays properly
          const searchParams = new URLSearchParams();
          for (const [key, value] of Object.entries(params)) {
            if (Array.isArray(value)) {
              value.forEach((val) => searchParams.append(key, val));
            } else if (value !== undefined) {
              searchParams.append(key, value as string);
            }
          }
          return searchParams.toString();
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getQuestionById: async (id: string): Promise<Question> => {
    try {
      const response = await axiosInstance.get<Question>(`/api/questions/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createQuestion: async (question: Question): Promise<Question> => {
    try {
      const response = await axiosInstance.post<Question>('/api/questions', question);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateQuestion: async (question: Question): Promise<Question> => {
    try {
      const response = await axiosInstance.put<Question>('/api/questions', question);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteQuestion: async (id: string): Promise<void> => {
    try {
      await axiosInstance.delete(`/api/questions/${id}`);
    } catch (error) {
      throw error;
    }
  },

  getQuestionsByCategory: async (
    category: string, 
    page: number = 0, 
    size: number = 10
  ): Promise<PaginatedQuestionResponse> => {
    try {
      const response = await axiosInstance.get<PaginatedQuestionResponse>(
        `/api/questions/category/${category}`,
        { params: { page, size } }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getUnansweredQuestionsByCategory: async (params: UnansweredQuestionsParams): Promise<Question[]> => {
    try {
      const response = await axiosInstance.get<Question[]>('/api/questions/unanswered', {
        params: {
          userId: params.userId,
          category: params.category,
          limit: params.limit || 10,
          difficulty: params.difficulty
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  uploadQuestionPhotos: async (
    questionId: string, 
    qFile: File, 
    aFile: File
  ): Promise<string> => {
    const formData = new FormData();
    formData.append('question-file', qFile);
    formData.append('answer-file', aFile);
    
    try {
      const response = await axiosInstance.post(
        `/api/questions/${questionId}/add-photo`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
               transformRequest: [(data) => data],
        }
      );
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to upload question photos');
      }
      throw new Error('Unexpected error occurred');
    }
  },

   clearQuestionRecord: async (): Promise<void> => {
    try {
      await axiosInstance.post(`/api/questions/clear-record`);
    } catch (error) {
      throw error;
    }
  },

  verifyQuestion: async (id: string): Promise<Question> => {
    try {
      const response = await axiosInstance.put<Question>(`/api/questions/${id}/approve`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};